import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY } from './constants';

type AdvisorDashNavContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const AdvisorDashNavContext = createContext<AdvisorDashNavContextValue | null>(null);

export function useAdvisorDashNav() {
  const context = useContext(AdvisorDashNavContext);
  if (!context) {
    throw new Error('useAdvisorDashNav must be used within a AdvisorDashNavProvider');
  }
  return context;
}

/**
 * Context to make interactions with `AdvisorDashTopNavLayout` possible in a higher level in the component tree.
 * Specifically, to allow the AI sidebar to close the left nav sidebar when it's opened.
 *
 * Due to the AI requiring being at the highest level in the component tree to avoid re-renders on page changes,
 * this context is necessary to avoid having to move the SidebarProvider at the `_app.tsx` component as that
 * affects all the app, even the pages only accessible for "Client" users without access to AI or the Sidebar.
 */
const AUTO_COLLAPSE_BREAKPOINT = 1024;

export function AdvisorDashNavProvider({ children }: React.PropsWithChildren) {
  const router = useRouter();
  const currentPath = router.pathname;

  const pathsToDefaultCollapse = ['/advisor/dashboard/marketing/'];
  const exactPathsToDefaultCollapse = ['/advisor/dashboard/tasks/new'];
  const isDefaultCollapsed =
    exactPathsToDefaultCollapse.includes(currentPath) ||
    currentPath.startsWith('/advisor/dashboard/tasks/new/') ||
    // A household deep-link goes straight into a Canvas session, which collapses
    // the nav anyway. Starting collapsed means the content area never slides
    // sideways mid-load — collapsing later registers as a layout shift no matter
    // how early it fires.
    currentPath.startsWith('/advisor/canvas/household/') ||
    pathsToDefaultCollapse.some((path) => currentPath.includes(path));

  const [open, setOpen] = useState(
    !isDefaultCollapsed && sessionStorage.getItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY) !== 'false',
  );

  useEffect(() => {
    const checkWindowSize = () => {
      if (window.innerWidth < AUTO_COLLAPSE_BREAKPOINT) {
        setOpen(false);
      }
      if (
        window.innerWidth >= AUTO_COLLAPSE_BREAKPOINT &&
        sessionStorage.getItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY) !== 'false'
      ) {
        setOpen(true);
      }
    };

    if (!isDefaultCollapsed) {
      checkWindowSize();
    }

    window.addEventListener('resize', checkWindowSize);

    return () => {
      window.removeEventListener('resize', checkWindowSize);
    };
  }, []);

  useEffect(() => {
    if (isDefaultCollapsed) {
      setOpen(false);
    } else if (
      window.innerWidth >= AUTO_COLLAPSE_BREAKPOINT &&
      sessionStorage.getItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY) !== 'false'
    ) {
      setOpen(true);
    }
  }, [currentPath, isDefaultCollapsed]);

  const value = useMemo(() => ({ open, setOpen }), [open, setOpen]);

  return <AdvisorDashNavContext.Provider value={value}>{children}</AdvisorDashNavContext.Provider>;
}
